import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Building, ArrowLeft, ArrowRight, Leaf, CheckCircle2, AlertCircle, Info } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import StepNavigation from "@/components/StepNavigation";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEPARTMENTS = [
  "01", "02", "03", "04", "05", "06", "07", "08", "09", "10",
  "11", "12", "13", "14", "15", "16", "17", "18", "19", "21",
  "22", "23", "24", "25", "26", "27", "28", "29", "2A", "2B",
  "30", "31", "32", "33", "34", "35", "36", "37", "38", "39",
  "40", "41", "42", "43", "44", "45", "46", "47", "48", "49",
  "50", "51", "52", "53", "54", "55", "56", "57", "58", "59",
  "60", "61", "62", "63", "64", "65", "66", "67", "68", "69",
  "70", "71", "72", "73", "74", "75", "76", "77", "78", "79",
  "80", "81", "82", "83", "84", "85", "86", "87", "88", "89",
  "90", "91", "92", "93", "94", "95", "971", "972", "973", "974", "976"
];

const WORK_TYPES_COPRO = [
  { id: "isolation_toiture", label: "Isolation toiture / combles" },
  { id: "isolation_facades", label: "Isolation façades (ITE)" },
  { id: "isolation_plancher_bas", label: "Isolation plancher bas" },
  { id: "menuiseries_parties_communes", label: "Menuiseries parties communes" },
  { id: "chauffage_collectif", label: "Chauffage collectif" },
  { id: "ventilation_collective", label: "Ventilation collective (VMC)" },
  { id: "calorifugeage", label: "Calorifugeage réseaux" },
  { id: "pilotage_chauffage", label: "Pilotage du chauffage" },
  { id: "renovation_globale", label: "Rénovation globale" }
];

const DPE_CLASSES = [
  { value: "G", label: "G - Très énergivore (> 450 kWh/m²)" },
  { value: "F", label: "F - Passoire thermique (331-450 kWh/m²)" },
  { value: "E", label: "E - Énergivore (251-330 kWh/m²)" },
  { value: "D", label: "D - Standard (151-250 kWh/m²)" },
  { value: "C", label: "C - Correct (91-150 kWh/m²)" },
  { value: "B", label: "B - Bon (51-90 kWh/m²)" },
  { value: "A", label: "A - Excellent (≤ 50 kWh/m²)" }
];

// Définition des étapes pour la navigation
const STEPS = [
  { id: 1, title: "Copropriété" },
  { id: 2, title: "Contact" },
  { id: 3, title: "Bâtiment" },
  { id: 4, title: "Travaux" },
  { id: 5, title: "Récapitulatif" }
];

export default function CoproprieteForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    beneficiary_type: "copropriete",
    // Copropriété info
    copro_name: "",
    copro_address: "",
    postal_code: "",
    city: "",
    department: "",
    // Syndic
    syndic_name: "",
    syndic_type: "professionnel",
    // Contact représentant
    contact_first_name: "",
    contact_last_name: "",
    email: "",
    phone: "",
    // Bâtiment
    housing_type: "batiment",
    total_lots: "",
    residence_principale_ratio: "",
    building_age: "",
    housing_surface: "", // Surface totale parties communes
    housing_age: "",
    // Registre & AMO
    is_registered: false,
    registration_number: "",
    has_amo: false,
    is_fragile: false,
    // DPE
    dpe_collective_current: "",
    dpe_collective_target: "",
    energy_gain_target: "",
    // Travaux
    work_types: [],
    // Projet
    urgency: "6_mois",
    ag_vote_date: "",
    additional_info: ""
  });

  const totalSteps = 5;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleWorkType = (workId) => {
    setFormData(prev => ({
      ...prev,
      work_types: prev.work_types.includes(workId)
        ? prev.work_types.filter(w => w !== workId)
        : [...prev.work_types, workId]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Derive department from postal code if not set
      const derivedDepartment = formData.department || (formData.postal_code ? formData.postal_code.substring(0, 2) : "00");
      
      // Préparer les données
      const submitData = {
        ...formData,
        // Map copro_address to address for API compatibility
        address: formData.copro_address || formData.copro_name,
        // Ensure department is set
        department: derivedDepartment,
        total_lots: parseInt(formData.total_lots) || 0,
        residence_principale_ratio: parseInt(formData.residence_principale_ratio) || 0,
        building_age: parseInt(formData.building_age) || 0,
        housing_surface: parseInt(formData.housing_surface) || 0,
        housing_age: parseInt(formData.building_age) || 0,
        energy_gain_target: parseInt(formData.energy_gain_target) || 0,
        // Convert empty strings to null for optional DPE fields
        dpe_collective_current: formData.dpe_collective_current || null,
        dpe_collective_target: formData.dpe_collective_target || null,
        // Convert empty strings to null for other optional fields
        registration_number: formData.registration_number || null,
        ag_vote_date: formData.ag_vote_date || null,
        additional_info: formData.additional_info || null
      };

      const response = await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Erreur lors de la soumission");
      }

      const result = await response.json();
      toast.success("Simulation terminée !");
      navigate(`/resultat/${result.id}`);
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.message || "Erreur lors de la soumission");
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    // Validation par étape
    if (step === 1) {
      if (!formData.copro_name || !formData.syndic_name) {
        toast.error("Veuillez remplir les informations de la copropriété");
        return;
      }
    }
    if (step === 2) {
      if (!formData.contact_first_name || !formData.contact_last_name || !formData.email) {
        toast.error("Veuillez remplir les informations de contact");
        return;
      }
    }
    if (step === 3) {
      if (!formData.total_lots || !formData.building_age) {
        toast.error("Veuillez remplir les informations du bâtiment");
        return;
      }
    }
    if (step === 4 && formData.work_types.length === 0) {
      toast.error("Veuillez sélectionner au moins un type de travaux");
      return;
    }
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  // Calcul indicatif du gain énergétique
  const estimatedEnergyGain = () => {
    const current = DPE_CLASSES.findIndex(d => d.value === formData.dpe_collective_current);
    const target = DPE_CLASSES.findIndex(d => d.value === formData.dpe_collective_target);
    if (current >= 0 && target >= 0 && target > current) {
      // Estimation simplifiée
      const diff = target - current;
      return Math.min(35 + (diff * 10), 70);
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-home">
            <Leaf className="h-7 w-7 text-[#1A4D2E]" />
            <span className="text-xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              MesSubventions
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Building className="h-5 w-5 text-[#1A4D2E]" />
            <span className="font-medium text-slate-700">Copropriété</span>
          </div>
        </div>
      </header>

      {/* Progress bar - Navigation cliquable */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <StepNavigation
            steps={STEPS}
            currentStep={step}
            onStepClick={(s) => setStep(s)}
            className="mb-3"
          />
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#1A4D2E] transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
          <p className="text-sm text-slate-500 mt-2 text-center">
            Étape {step} sur {totalSteps}: {STEPS[step - 1].title}
            {step > 1 && (
              <span className="text-[#1A4D2E] ml-2">(cliquez sur une étape précédente pour y revenir)</span>
            )}
          </p>
        </div>
      </div>

      {/* Form content - Enveloppé dans <form> pour l'autocomplétion navigateur */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <form autoComplete="on" onSubmit={(e) => e.preventDefault()}>
        {/* Étape 1: Copropriété */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-copro-info">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-[#1D1D1D] mb-2">
                Informations de la copropriété
              </h1>
              <p className="text-slate-500">
                Ces informations nous permettent d'identifier votre copropriété
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
              <div className="space-y-2">
                <Label htmlFor="copro_name">Nom de la copropriété *</Label>
                <Input
                  id="copro_name"
                  value={formData.copro_name}
                  onChange={(e) => updateField("copro_name", e.target.value)}
                  placeholder="Ex: Résidence Les Lilas"
                  className="h-12"
                  data-testid="input-copro-name"
                />
              </div>

              <div className="space-y-2">
                <Label>Adresse de l'immeuble *</Label>
                <AddressAutocomplete
                  value={formData.copro_address}
                  onChange={(value) => updateField("copro_address", value)}
                  onSelect={(addressData) => {
                    setFormData(prev => ({
                      ...prev,
                      copro_address: addressData.address,
                      postal_code: addressData.postal_code,
                      city: addressData.city,
                      department: addressData.department
                    }));
                  }}
                  placeholder="Commencez à taper l'adresse de l'immeuble..."
                  data-testid="input-copro-address"
                />
                <p className="text-xs text-slate-400 mt-1">
                  💡 L'adresse, le code postal, la ville et le département se rempliront automatiquement
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Code postal *</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => updateField("postal_code", e.target.value)}
                    placeholder="75001"
                    className="h-12"
                    autoComplete="postal-code"
                    data-testid="input-postal"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">Ville *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => updateField("city", e.target.value)}
                    placeholder="Paris"
                    className="h-12"
                    data-testid="input-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Département *</Label>
                  <Select 
                    value={formData.department} 
                    onValueChange={(v) => updateField("department", v)}
                  >
                    <SelectTrigger className="h-12" data-testid="select-department">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map(dep => (
                        <SelectItem key={dep} value={dep}>{dep}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 mt-4">
                <h3 className="font-semibold text-[#1D1D1D] mb-3">Syndic</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="syndic_name">Nom du syndic *</Label>
                    <Input
                      id="syndic_name"
                      value={formData.syndic_name}
                      onChange={(e) => updateField("syndic_name", e.target.value)}
                      placeholder="Ex: Cabinet Martin"
                      className="h-12"
                      data-testid="input-syndic-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type de syndic</Label>
                    <Select 
                      value={formData.syndic_type} 
                      onValueChange={(v) => updateField("syndic_type", v)}
                    >
                      <SelectTrigger className="h-12" data-testid="select-syndic-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professionnel">Professionnel</SelectItem>
                        <SelectItem value="benevole">Bénévole</SelectItem>
                        <SelectItem value="cooperatif">Coopératif</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Étape 2: Contact */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-contact">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-[#1D1D1D] mb-2">
                Vos coordonnées
              </h1>
              <p className="text-slate-500">
                En tant que représentant de la copropriété
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_first_name">Prénom *</Label>
                  <Input
                    id="contact_first_name"
                    value={formData.contact_first_name}
                    onChange={(e) => updateField("contact_first_name", e.target.value)}
                    placeholder="Jean"
                    className="h-12"
                    autoComplete="given-name"
                    data-testid="input-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_last_name">Nom *</Label>
                  <Input
                    id="contact_last_name"
                    value={formData.contact_last_name}
                    onChange={(e) => updateField("contact_last_name", e.target.value)}
                    placeholder="Dupont"
                    className="h-12"
                    autoComplete="family-name"
                    data-testid="input-lastname"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="syndic@copropriete.fr"
                    className="h-12"
                    autoComplete="email"
                    data-testid="input-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    placeholder="06 12 34 56 78"
                    className="h-12"
                    autoComplete="tel"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Étape 3: Bâtiment */}
        {step === 3 && (
          <div className="space-y-6" data-testid="step-building">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-[#1D1D1D] mb-2">
                Caractéristiques du bâtiment
              </h1>
              <p className="text-slate-500">
                Informations essentielles pour calculer vos aides
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="total_lots">Nombre de lots *</Label>
                  <Input
                    id="total_lots"
                    type="number"
                    value={formData.total_lots}
                    onChange={(e) => updateField("total_lots", e.target.value)}
                    placeholder="Ex: 30"
                    className="h-12"
                    data-testid="input-total-lots"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="building_age">Âge du bâtiment (années) *</Label>
                  <Input
                    id="building_age"
                    type="number"
                    value={formData.building_age}
                    onChange={(e) => updateField("building_age", e.target.value)}
                    placeholder="Ex: 40"
                    className="h-12"
                    data-testid="input-building-age"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="housing_surface">Surface totale (m²)</Label>
                  <Input
                    id="housing_surface"
                    type="number"
                    value={formData.housing_surface}
                    onChange={(e) => updateField("housing_surface", e.target.value)}
                    placeholder="Ex: 2500"
                    className="h-12"
                    data-testid="input-surface"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="residence_principale_ratio">
                  % de résidences principales *
                </Label>
                <Input
                  id="residence_principale_ratio"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.residence_principale_ratio}
                  onChange={(e) => updateField("residence_principale_ratio", e.target.value)}
                  placeholder="Ex: 85"
                  className="h-12"
                  data-testid="input-res-ratio"
                />
                <p className="text-xs text-slate-400">
                  Pour MPR Copro: minimum 75% (ou 65% si ≤ 20 lots)
                </p>
              </div>

              <div className="border-t border-slate-100 pt-4 mt-4">
                <h3 className="font-semibold text-[#1D1D1D] mb-3">Immatriculation & AMO</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="is_registered"
                      checked={formData.is_registered}
                      onCheckedChange={(c) => updateField("is_registered", c)}
                      data-testid="checkbox-registered"
                    />
                    <Label htmlFor="is_registered" className="cursor-pointer">
                      Copropriété immatriculée au registre national
                    </Label>
                  </div>
                  
                  {formData.is_registered && (
                    <div className="ml-7 space-y-2">
                      <Label htmlFor="registration_number">N° d'immatriculation</Label>
                      <Input
                        id="registration_number"
                        value={formData.registration_number}
                        onChange={(e) => updateField("registration_number", e.target.value)}
                        placeholder="Ex: AA00001234"
                        className="h-12"
                        data-testid="input-registration"
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="has_amo"
                      checked={formData.has_amo}
                      onCheckedChange={(c) => updateField("has_amo", c)}
                      data-testid="checkbox-amo"
                    />
                    <Label htmlFor="has_amo" className="cursor-pointer">
                      Assistance à Maîtrise d'Ouvrage (AMO) prévue ou en cours
                    </Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="is_fragile"
                      checked={formData.is_fragile}
                      onCheckedChange={(c) => updateField("is_fragile", c)}
                      data-testid="checkbox-fragile"
                    />
                    <Label htmlFor="is_fragile" className="cursor-pointer">
                      Copropriété en difficulté (impayés ≥ 8%)
                    </Label>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 mt-4">
                <h3 className="font-semibold text-[#1D1D1D] mb-3">Performance énergétique</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>DPE collectif actuel</Label>
                    <Select 
                      value={formData.dpe_collective_current} 
                      onValueChange={(v) => updateField("dpe_collective_current", v)}
                    >
                      <SelectTrigger className="h-12" data-testid="select-dpe-current">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {DPE_CLASSES.map(dpe => (
                          <SelectItem key={dpe.value} value={dpe.value}>{dpe.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>DPE collectif visé après travaux</Label>
                    <Select 
                      value={formData.dpe_collective_target} 
                      onValueChange={(v) => updateField("dpe_collective_target", v)}
                    >
                      <SelectTrigger className="h-12" data-testid="select-dpe-target">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {DPE_CLASSES.map(dpe => (
                          <SelectItem key={dpe.value} value={dpe.value}>{dpe.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <Label htmlFor="energy_gain_target">
                    Gain énergétique visé (%)
                    {estimatedEnergyGain() && (
                      <span className="ml-2 text-[#1A4D2E] text-sm font-normal">
                        (estimation: ~{estimatedEnergyGain()}%)
                      </span>
                    )}
                  </Label>
                  <Input
                    id="energy_gain_target"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.energy_gain_target}
                    onChange={(e) => updateField("energy_gain_target", e.target.value)}
                    placeholder="Ex: 45"
                    className="h-12"
                    data-testid="input-energy-gain"
                  />
                  <p className="text-xs text-slate-400">
                    Pour MPR Copro: minimum 35% de gain énergétique requis
                  </p>
                </div>
              </div>
            </div>

            {/* Info card */}
            {!formData.is_registered && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800">Immatriculation requise</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      L'immatriculation au registre national des copropriétés est obligatoire pour bénéficier de MaPrimeRénov' Copropriété.
                      <a href="https://www.registre-coproprietes.gouv.fr" target="_blank" rel="noopener noreferrer" className="underline ml-1">
                        Immatriculer ma copropriété
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Étape 4: Travaux */}
        {step === 4 && (
          <div className="space-y-6" data-testid="step-works">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-[#1D1D1D] mb-2">
                Travaux envisagés
              </h1>
              <p className="text-slate-500">
                Sélectionnez les travaux prévus pour votre copropriété
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {WORK_TYPES_COPRO.map((work) => (
                  <div
                    key={work.id}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.work_types.includes(work.id)
                        ? 'border-[#1A4D2E] bg-[#E8F5E9]'
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => toggleWorkType(work.id)}
                    data-testid={`work-type-${work.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={formData.work_types.includes(work.id)}
                        className="data-[state=checked]:bg-[#1A4D2E] data-[state=checked]:border-[#1A4D2E]"
                      />
                      <span className="font-medium">{work.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
              <div className="space-y-2">
                <Label>Urgence du projet</Label>
                <Select value={formData.urgency} onValueChange={(v) => updateField("urgency", v)}>
                  <SelectTrigger className="h-12" data-testid="select-urgency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediat">Immédiat - Travaux en cours</SelectItem>
                    <SelectItem value="3_mois">Sous 3 mois - Vote AG prévu</SelectItem>
                    <SelectItem value="6_mois">Sous 6 mois - En étude</SelectItem>
                    <SelectItem value="plus_tard">Plus tard - Prospection</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ag_vote_date">Date de vote en AG (si connue)</Label>
                <Input
                  id="ag_vote_date"
                  type="date"
                  value={formData.ag_vote_date}
                  onChange={(e) => updateField("ag_vote_date", e.target.value)}
                  className="h-12"
                  data-testid="input-ag-date"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="additional_info">Informations complémentaires</Label>
                <Textarea
                  id="additional_info"
                  value={formData.additional_info}
                  onChange={(e) => updateField("additional_info", e.target.value)}
                  placeholder="Décrivez votre projet, vos contraintes..."
                  className="min-h-[100px]"
                  data-testid="textarea-info"
                />
              </div>
            </div>
          </div>
        )}

        {/* Étape 5: Récapitulatif */}
        {step === 5 && (
          <div className="space-y-6" data-testid="step-summary">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-[#1D1D1D] mb-2">
                Récapitulatif
              </h1>
              <p className="text-slate-500">
                Vérifiez les informations avant de lancer la simulation
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-[#1A4D2E] mb-2">Copropriété</h3>
                  <div className="text-sm space-y-1 text-slate-600">
                    <p><span className="font-medium">Nom:</span> {formData.copro_name}</p>
                    <p><span className="font-medium">Adresse:</span> {formData.copro_address}, {formData.postal_code} {formData.city}</p>
                    <p><span className="font-medium">Syndic:</span> {formData.syndic_name} ({formData.syndic_type})</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A4D2E] mb-2">Contact</h3>
                  <div className="text-sm space-y-1 text-slate-600">
                    <p><span className="font-medium">Représentant:</span> {formData.contact_first_name} {formData.contact_last_name}</p>
                    <p><span className="font-medium">Email:</span> {formData.email}</p>
                    <p><span className="font-medium">Tél:</span> {formData.phone || "Non renseigné"}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <h3 className="font-semibold text-[#1A4D2E] mb-2">Bâtiment</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Nombre de lots</p>
                    <p className="font-medium">{formData.total_lots}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Âge</p>
                    <p className="font-medium">{formData.building_age} ans</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Rés. principales</p>
                    <p className="font-medium">{formData.residence_principale_ratio}%</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Gain énergétique</p>
                    <p className="font-medium">{formData.energy_gain_target || "N/A"}%</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.is_registered && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Immatriculée
                    </span>
                  )}
                  {formData.has_amo && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> AMO
                    </span>
                  )}
                  {formData.is_fragile && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                      Copropriété fragile
                    </span>
                  )}
                  {formData.dpe_collective_current && (
                    <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
                      DPE {formData.dpe_collective_current} → {formData.dpe_collective_target || "?"}
                    </span>
                  )}
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <h3 className="font-semibold text-[#1A4D2E] mb-2">Travaux envisagés</h3>
                <div className="flex flex-wrap gap-2">
                  {formData.work_types.map(workId => (
                    <span key={workId} className="px-3 py-1 bg-[#E8F5E9] text-[#1A4D2E] text-sm rounded-full">
                      {WORK_TYPES_COPRO.find(w => w.id === workId)?.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex gap-3">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Prochaine étape</p>
                  <p className="mt-1">
                    Après simulation, vous recevrez une estimation détaillée des aides disponibles 
                    (MaPrimeRénov' Copropriété, CEE collectifs, aides locales...) et pourrez être 
                    contacté par un conseiller France Rénov' spécialisé copropriétés.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          {step > 1 ? (
            <Button 
              variant="outline" 
              onClick={prevStep}
              className="gap-2"
              data-testid="btn-prev"
            >
              <ArrowLeft className="h-4 w-4" /> Précédent
            </Button>
          ) : (
            <Link to="/">
              <Button variant="outline" className="gap-2" data-testid="btn-back-home">
                <ArrowLeft className="h-4 w-4" /> Retour
              </Button>
            </Link>
          )}

          {step < totalSteps ? (
            <Button 
              type="button"
              onClick={nextStep}
              className="gap-2 bg-[#1A4D2E] hover:bg-[#143d24]"
              data-testid="btn-next"
            >
              Suivant <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button 
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className="gap-2 bg-[#E07A5F] hover:bg-[#c4624a]"
              data-testid="btn-submit"
            >
              {loading ? "Simulation en cours..." : "Lancer la simulation"}
            </Button>
          )}
        </div>
        </form>
      </main>
    </div>
  );
}
